import * as amplitude from '@amplitude/analytics-browser';
import { EventProperties, UserProperties } from './eventTypes';

/**
 * Diagnosing "no data in Amplitude dashboard":
 * 1. initAmplitude() must run once (e.g. in App.tsx on mount). Without it, events are not sent.
 * 2. In DevTools → Network, filter by "amplitude" or "api2.amplitude.com". After init + first event you should see POST requests (batch or event).
 * 3. Set AMPLITUDE_DEBUG_EVENTS = true below to log every event in the console (dev only).
 * 4. Confirm the API key matches the project in Amplitude (Settings → Projects).
 */

// Constants
const AMPLITUDE_API_KEY = 'b90cd1e46e115b8c791620b0a292a7a5';
const APP_NAME = 'AuditLogs';

const isDev = () =>
  typeof process !== 'undefined' &&
  process.env?.NODE_ENV === 'development';

/** Set to true to log every Amplitude event in console (dev only). */
const AMPLITUDE_DEBUG_EVENTS = false;

/**
 * Initializes Amplitude with default configuration.
 * Must be called once before any track/setUserId calls (e.g. on app mount).
 */
export const initAmplitude = () => {
  amplitude.init(AMPLITUDE_API_KEY, undefined, {
    defaultTracking: {
      sessions: true,
      pageViews: true,
    },
  });
  if (isDev()) {
    console.info(
      '[Amplitude] Initialized. Verify in Network tab: filter by "amplitude" or "api2.amplitude.com".'
    );
  }
};

/**
 * Creates standardized event properties with the app name included.
 *
 * @param baseProperties - The base event properties
 * @returns EventProperties with app name included
 */
const createEventProperties = (
  baseProperties: Omit<EventProperties, 'app'>
): EventProperties => {
  return {
    ...baseProperties,
    app: APP_NAME,
  };
};

/**
 * Tracks an event in Amplitude with error safety and standardized properties.
 *
 * @param userAndCompany - The user identifier (format: user@company)
 * @param userProperties - User-specific properties (company, user)
 * @param eventType - The name of the event (e.g., "UserInterfaceFormLoad")
 * @param eventProperties - Event properties (elapsed time, title, component name, version)
 */
export const logAmplitudeEventWithProps = (
  userAndCompany: string,
  userProperties: UserProperties,
  eventType: string,
  eventProperties: Omit<EventProperties, 'app'>
) => {
  try {
    // Set user ID
    amplitude.setUserId(userAndCompany);

    // Set user properties
    const identify = new amplitude.Identify();
    Object.entries(userProperties).forEach(([key, value]) => {
      identify.set(key, value);
    });
    amplitude.identify(identify);

    // Create standardized event properties with app name
    const completeEventProperties = createEventProperties(eventProperties);

    amplitude.track(eventType, completeEventProperties);
    if (isDev() && AMPLITUDE_DEBUG_EVENTS) {
      console.debug('[Amplitude] Event:', eventType, completeEventProperties);
    }
  } catch (error) {
    console.error('Amplitude tracking error:', error);
  }
};

/**
 * Helper function to create standardized event properties with common defaults.
 *
 * @param title - The event title
 * @param componentName - The component name
 * @param version - The app version
 * @param elapsed_ms - The elapsed time (defaults to "124")
 * @returns EventProperties object with app name included
 */
export const createAmplitudeEventProperties = (
  title: string,
  componentName: string,
  version: string,
  elapsed_ms: string = '124'
): Omit<EventProperties, 'app'> => {
  return {
    elapsed_ms,
    title,
    componentName,
    version,
  };
};

export const setAmplitudeUser = (userId: string, companyId: string) => {
  amplitude.setUserId(userId);
  amplitude.setGroup('company', companyId);
};

/** Event name for when a user successfully enters the Audit Logs app (after login or auto-login). */
export const APP_ENTERED_EVENT = 'AuditLogs App Entered';

/** Event name for when a user logs out. */
export const LOGOUT_EVENT = 'Audit Logout';

/** Event name for when a user toggles light/dark theme. */
export const THEME_TOGGLED_EVENT = 'Audit Theme Toggled';

export type AppEntryPoint = 'web' | 'desktop';

/**
 * Tracks that a user has successfully entered the Audit Logs application.
 * Call once after successful login (web) or after successful auto-login (desktop).
 * Use entry_point to segment users by web vs desktop in Amplitude.
 */
export const logAppEntered = (
  userId: string,
  companyId: string,
  entryPoint: AppEntryPoint,
  version?: string
) => {
  try {
    amplitude.setUserId(userId);
    amplitude.setGroup('company', companyId);
    const identify = new amplitude.Identify();
    identify.set('company', companyId);
    identify.set('user', userId);
    amplitude.identify(identify);

    // Breakpoint: pause here to verify event is sent (remove when done debugging)
    //debugger;
    amplitude.track(APP_ENTERED_EVENT, {
      app: APP_NAME,
      entry_point: entryPoint,
      ...(version && { version }),
    });
    if (isDev() && AMPLITUDE_DEBUG_EVENTS) {
      console.debug('[Amplitude] Event:', APP_ENTERED_EVENT, { entry_point: entryPoint, version });
    }
  } catch (error) {
    console.error('Amplitude tracking error (logAppEntered):', error);
  }
};

export const logAmplitudeEventWithPayload = (
  eventName: string,
  payload?: object
) => {
  amplitude.track(eventName, payload);
  if (isDev() && AMPLITUDE_DEBUG_EVENTS) {
    console.debug('[Amplitude] Event:', eventName, payload);
  }
};
export const logAmplitudeEvent = (eventName: string, eventHandler: string) => {
  amplitude.track(eventName, {
    buttonName: eventHandler,
  });
};
export const logAmplitudeException = (
  error: unknown,
  context: Record<string, any> = {}
) => {
  const payload = {
    message: error instanceof Error ? error.message : JSON.stringify(error),
    stack: error instanceof Error ? error.stack : undefined,
    ...context,
  };
  //logAmplitudeEventWithPayload("Exception", payload);
};

export default amplitude;
